export const REDUCED_MOTION_PREFERENCE_EVENT = "critter-reduced-motion-preference";
export const REDUCED_MOTION_PREFERENCE_KEY = "critter.reducedMotion";

const REDUCED_MOTION_CLASS = "critter-reduced-motion";
const REDUCED_MOTION_MEDIA_QUERY = "(prefers-reduced-motion: reduce)";

function systemPrefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(REDUCED_MOTION_MEDIA_QUERY).matches
  );
}

export function readReducedMotionPreference(): boolean {
  if (typeof window === "undefined") return false;

  try {
    const stored = window.localStorage.getItem(REDUCED_MOTION_PREFERENCE_KEY);
    if (stored === "true") return true;
    if (stored === "false") return false;
  } catch {
    // Ignore localStorage failures; fall back to the system preference.
  }

  return systemPrefersReducedMotion();
}

export function applyReducedMotionDocumentClass(enabled = readReducedMotionPreference()): boolean {
  if (typeof document === "undefined") return enabled;
  document.documentElement.classList.toggle(REDUCED_MOTION_CLASS, !!enabled);
  return !!enabled;
}

export function setReducedMotionPreference(enabled: boolean): boolean {
  const next = !!enabled;
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(REDUCED_MOTION_PREFERENCE_KEY, String(next));
    } catch {
      // Ignore localStorage failures; the current document still receives the setting.
    }
  }
  applyReducedMotionDocumentClass(next);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(REDUCED_MOTION_PREFERENCE_EVENT, { detail: { enabled: next } })
    );
  }
  return next;
}

export function subscribeReducedMotionPreference(listener: (enabled: boolean) => void): () => void {
  if (typeof window === "undefined") return () => {};

  const notify = () => listener(readReducedMotionPreference());
  const preferenceHandler = (event: Event) => {
    const detail = event instanceof CustomEvent ? event.detail : null;
    listener((detail?.enabled ?? readReducedMotionPreference()) === true);
  };
  const mediaQuery =
    typeof window.matchMedia === "function" ? window.matchMedia(REDUCED_MOTION_MEDIA_QUERY) : null;
  const mediaHandler = () => {
    try {
      if (window.localStorage.getItem(REDUCED_MOTION_PREFERENCE_KEY) == null) notify();
    } catch {
      notify();
    }
  };

  window.addEventListener(REDUCED_MOTION_PREFERENCE_EVENT, preferenceHandler);
  mediaQuery?.addEventListener?.("change", mediaHandler);
  mediaQuery?.addListener?.(mediaHandler);
  applyReducedMotionDocumentClass();

  return () => {
    window.removeEventListener(REDUCED_MOTION_PREFERENCE_EVENT, preferenceHandler);
    mediaQuery?.removeEventListener?.("change", mediaHandler);
    mediaQuery?.removeListener?.(mediaHandler);
  };
}
